const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

export const isPushSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

interface PermissionResult {
  status: NotificationPermission | 'unsupported';
  message: string;
}

export const requestNotificationPermission = async (): Promise<PermissionResult> => {
  if (!('Notification' in window)) {
    return { status: 'unsupported', message: 'Les notifications ne sont pas supportées par ce navigateur' };
  }

  const permission = await Notification.requestPermission();
  return { status: permission, message: getPermissionMessage(permission) };
};

const getPermissionMessage = (permission: NotificationPermission): string => {
  switch (permission) {
    case 'granted':
      return 'Notifications activées !';
    case 'denied':
      return 'Notifications refusées. Vous pouvez les activer dans les paramètres du navigateur.';
    case 'default':
      return 'Permission en attente';
    default:
      return 'Statut inconnu';
  }
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker non supporté');
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[Push] Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    throw error;
  }
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

export const subscribeToPush = async (registration: ServiceWorkerRegistration): Promise<PushSubscription> => {
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('VAPID public key not configured');
  }

  try {
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      console.log('[Push] Already subscribed');
      return subscription;
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });

    console.log('[Push] New subscription created');
    return subscription;
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    throw error;
  }
};

export const unsubscribeFromPush = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed successfully');
      return true;
    }

    return false;
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    throw error;
  }
};

export const getCurrentSubscription = async (): Promise<PushSubscription | null> => {
  try {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000));

    const subscriptionPromise = (async () => {
      const registration = await navigator.serviceWorker.ready;
      return await registration.pushManager.getSubscription();
    })();

    return await Promise.race([subscriptionPromise, timeoutPromise]);
  } catch (error: any) {
    console.log('[Push] Error getting subscription:', error.message);
    return null;
  }
};

interface PushSetupResult {
  success: boolean;
  error?: string;
  permission?: string;
  subscription: PushSubscriptionJSON | null;
}

export const setupPushNotifications = async (): Promise<PushSetupResult> => {
  if (!isPushSupported()) {
    return {
      success: false,
      error: 'Push notifications not supported',
      subscription: null,
    };
  }

  const { status } = await requestNotificationPermission();
  if (status !== 'granted') {
    return {
      success: false,
      error: 'Permission denied',
      permission: status,
      subscription: null,
    };
  }

  try {
    const registration = await registerServiceWorker();
    await navigator.serviceWorker.ready;
    const subscription = await subscribeToPush(registration);

    return {
      success: true,
      permission: 'granted',
      subscription: subscription.toJSON(),
    };
  } catch (error: any) {
    console.error('[Push] Setup failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to setup push notifications',
      permission: 'granted',
      subscription: null,
    };
  }
};
